import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import colors from "styles/colors";
import typography from "styles/typography";
import Text from "new_components/atoms/Text";

export default function CategorySelector({
  categories,
  onSelect,       // function(categoryName)
  addCategory,    // function(newCategoryName) - Now triggers the type selection
  currentValue,   // the string currently selected
}) {
  const [inputValue, setInputValue] = useState(currentValue || "");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Filter categories as the user types
  const filtered = categories.filter((cat) =>
    cat.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Whenever our parent changes the "currentValue," sync our local input
  useEffect(() => {
    setInputValue(currentValue || "");
  }, [currentValue]);

  // Add event listener for clicks outside the component
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    // Add the event listener when dropdown is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    
    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Called when user selects an *existing* category
  const handleSelect = (categoryName) => {
    onSelect(categoryName);
    setInputValue(categoryName);
    setIsOpen(false);
  };

  // Called when user clicks "Create 'xyz'"
  const handleCreate = () => {
    // Now this just triggers the type selection dialog in the parent
    addCategory(inputValue);
    setIsOpen(false);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Input
        placeholder="Select or create category"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && (
        <Dropdown>
          {/* Existing categories (filtered) */}
          {filtered.map((cat) => (
            <DropdownItem
              key={cat.name}
              onMouseDown={(e) => {
                e.preventDefault(); // avoid losing focus before we fire
                handleSelect(cat.name);
              }}
            >
              <CategoryName>{cat.name}</CategoryName>
              {cat.type && (
                <CategoryType>
                  {cat.type === "text" ? "Text" : "Date"}
                </CategoryType>
              )}
            </DropdownItem>
          ))}

          {/* Option to create a new category if inputValue is not blank and doesn't match an existing one */}
          {inputValue &&
            !categories.some((c) => c.name.toLowerCase() === inputValue.toLowerCase()) && (
              <DropdownItem
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCreate();
                }}
                style={{ fontStyle: "italic" }}
              >
                Create "{inputValue}"
              </DropdownItem>
            )}
        </Dropdown>
      )}
    </Wrapper>
  );
}

/** --- styled components --- **/
const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${colors.text.gray};
  border-radius: 4px;
`;

const Dropdown = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background-color: ${colors.background.white};
  border: 1px solid ${colors.text.gray};
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 10;
`;

const DropdownItem = styled.li`
  padding: 8px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:hover {
    background-color: ${colors.background.lightGray};
  }
`;

const CategoryName = styled.span`
  font-family: ${typography.body.regular.fontFamily};
  font-size: ${typography.body.regular.fontSize};
`;

const CategoryType = styled.span`
  font-family: ${typography.smallText.regular.fontFamily};
  font-size: ${typography.smallText.regular.fontSize};
  color: ${colors.text.gray};
  background-color: ${colors.neutral[100]};
  padding: 2px 6px;
  border-radius: 4px;
`;
